import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Spline from '@splinetool/react-spline';
import { optimizeSupabaseImage } from '../utils/imageOptimizer';
import TiltCard from '../components/ui/TiltCard';

import {
    Store,
    Sparkles,
    ShoppingBag,
    TrendingUp,

    CheckCircle2,
    ArrowRight,
    Users,
    BarChart3,
    HelpCircle,
    Info,
    Globe,
    MessageSquare,
    Mail,
    QrCode,
    Wand2,
    Calculator
} from 'lucide-react';
import ErrorBoundary from '../components/ErrorBoundary';

const SplineFallback = () => {
    return (
        <div className="relative w-full h-full flex items-center justify-center pointer-events-none">
            {/* Círculos decorativos de fondo con gradientes premium y animaciones de latido */}
            <motion.div 
                animate={{ 
                    scale: [1, 1.05, 1],
                    rotate: [0, 45, 0]
                }}
                transition={{ 
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
                className="absolute w-[280px] h-[280px] lg:w-[450px] lg:h-[450px] rounded-full bg-gradient-to-tr from-sky-400/20 to-indigo-500/20 blur-3xl"
            />
            <motion.div 
                animate={{ 
                    scale: [1, 1.08, 1],
                    rotate: [0, -45, 0]
                }}
                transition={{ 
                    duration: 12,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2
                }}
                className="absolute w-[220px] h-[220px] lg:w-[350px] lg:h-[350px] rounded-full bg-gradient-to-br from-indigo-400/10 to-pink-500/10 blur-2xl"
            />
            
            {/* Teléfono Mockup de Glassmorphism flotante que simula la tienda SkyMenu */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ 
                    y: [-10, 10, -10],
                    opacity: 1
                }}
                transition={{ 
                    y: {
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut"
                    },
                    opacity: { duration: 0.8 }
                }}
                className="relative z-10 w-[260px] h-[520px] lg:w-[300px] lg:h-[600px] rounded-[40px] border-4 border-white/30 bg-white/10 backdrop-blur-xl shadow-2xl p-4 flex flex-col justify-between overflow-hidden"
                style={{
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 0 rgba(255, 255, 255, 0.4)'
                }}
            >
                {/* Cámara superior / Isla dinámica */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-4 rounded-full bg-black/60 z-20" />

                {/* Contenido simulado de SkyMenu */}
                <div className="flex-1 flex flex-col pt-8">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                        <div className="w-16 h-4 rounded bg-white/20" />
                        <div className="w-8 h-8 rounded-full bg-sky-500/30 flex items-center justify-center text-xs text-white font-bold">SM</div>
                    </div>

                    {/* Categorías */}
                    <div className="flex gap-2 overflow-x-hidden mb-6">
                        <div className="px-3 py-1.5 rounded-full bg-sky-500/30 text-[10px] text-white font-semibold whitespace-nowrap">🌟 Destacados</div>
                        <div className="px-3 py-1.5 rounded-full bg-white/10 text-[10px] text-white/60 whitespace-nowrap">🍕 Pizzas</div>
                        <div className="px-3 py-1.5 rounded-full bg-white/10 text-[10px] text-white/60 whitespace-nowrap">🍔 Burgers</div>
                    </div>

                    {/* Lista de Productos simulada */}
                    <div className="space-y-4 flex-1 overflow-hidden">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex gap-3">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-sky-400/20 to-indigo-500/20 flex items-center justify-center text-lg">
                                    {i === 1 ? '🍕' : i === 2 ? '🍔' : '🍹'}
                                </div>
                                <div className="flex-1 flex flex-col justify-center space-y-1">
                                    <div className="h-3 w-3/4 rounded bg-white/20" />
                                    <div className="h-2 w-1/2 rounded bg-white/10" />
                                    <div className="flex items-center justify-between pt-1">
                                        <div className="h-3 w-1/4 rounded bg-sky-400/40" />
                                        <div className="w-5 h-5 rounded-full bg-sky-500/30 flex items-center justify-center text-white text-[10px]">+</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer flotante */}
                <div className="mt-4 p-3 rounded-2xl bg-sky-500/20 border border-sky-400/30 flex items-center justify-between backdrop-blur-md">
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-sky-400/40 flex items-center justify-center text-xs">🛒</div>
                        <div className="text-[10px] text-white font-semibold">Ver Pedido (2)</div>
                    </div>
                    <div className="text-xs font-bold text-sky-300">$14.50</div>
                </div>
            </motion.div>
        </div>
    );
};

export default function HomePage() {
    const { scrollY } = useScroll();
    const yText = useTransform(scrollY, [0, 500], [0, 100]);
    const opacityText = useTransform(scrollY, [0, 300], [1, 0]);
    const ySpline = useTransform(scrollY, [0, 500], [0, -50]);

    const features = [
        {
            icon: Store,
            title: "Tienda Virtual Completa",
            description: "Crea tu menú digital profesional en minutos. Sin complicaciones, sin costos ocultos."
        },
        {
            icon: ShoppingBag,
            title: "Sistema de Pedidos",
            description: "Gestiona pedidos en tiempo real con notificaciones automáticas y seguimiento."
        },
        {
            icon: Sparkles,
            title: "Búsqueda con IA",
            description: "Inteligencia artificial para ayudar a tus clientes a encontrar lo que buscan."
        },
        {
            icon: TrendingUp,
            title: "Dashboard Analítico",
            description: "Visualiza tus ventas, productos más vendidos y el crecimiento de tu negocio."
        },
        {
            icon: Globe,
            title: "Dominios Personalizados",
            description: "Tu propia dirección web única (e.j. tunegocio.skymenuapp.com) para fortalecer tu marca."
        },
        {
            icon: Users,
            title: "Sistema de Referidos",
            description: "Crece con el boca a boca. Programa de referidos integrado para expandir tu negocio."
        }
    ];

    const benefits = [
        "Sin costos de instalación",
        "Sin límite de productos",
        "Actualizaciones automáticas",
        "Soporte técnico incluido",
        "Optimizado para móviles",
        "Personalización completa"
    ];

    const faqs = [
        {
            question: "¿Funciona si mis clientes tienen mala conexión?",
            answer: "¡Sí! SkyMenu es una Progressive Web App (PWA) ultra-rápida. Además, todos nuestros planes incluyen la opción de generar un Catálogo PDF ligero que tus clientes pueden descargar."
        },
        {
            question: "¿Qué funcionalidades incluye el plan Básico?",
            answer: "El plan SkyBronze es muy completo: catálogo ilimitado, gestión de pedidos e inventario, cupones de descuento y estadísticas. Perfecto para empezar por solo $10 USD al mes."
        },
        {
            question: "¿Puedo personalizar el diseño de mi tienda?",
            answer: "Absolutamente. Puedes subir tu logo, portada, elegir los colores de tu marca y, en el plan Pro, crear contenido profesional para redes sociales con nuestro Marketing Studio."
        },
        {
            question: "¿Necesito instalar una aplicación?",
            answer: "No es obligatorio. SkyMenu funciona directamente en el navegador de cualquier teléfono o PC. Sin embargo, tú y tus clientes pueden 'instalarla' como una App para acceder más rápido."
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-sky-100 relative">
            
            {/* Header/Navbar */}
            <nav className="bg-white/80 backdrop-blur-md border-b border-sky-100 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center">
                            <img
                                src="https://imgur.com/WY80a5F.png"
                                alt="SkyMenu Logo"
                                className="h-20 w-auto"
                            />
                        </Link>
                        <div className="flex items-center gap-4">
                            <Link
                                to="/herramientas/qr"
                                className="text-gray-700 hover:text-sky-600 transition-colors font-medium"
                            >
                                <span className="hidden sm:inline">Herramientas Gratuitas</span>
                                <span className="sm:hidden text-sky-600">Herramientas</span>
                            </Link>
                            <Link
                                to="/pricing"
                                className="text-gray-700 hover:text-sky-600 transition-colors font-medium"
                            >
                                Precios
                            </Link>
                            <a
                                href="#home-support"
                                className="text-gray-700 hover:text-sky-600 transition-colors font-medium hidden md:block"
                            >
                                Soporte
                            </a>
                            <Link
                                to="/login"
                                className="px-6 py-2 bg-gradient-to-r from-sky-600 to-blue-600 text-white rounded-full hover:shadow-lg hover:scale-105 transition-all font-medium"
                            >
                                Iniciar Sesión
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main role="main">
                {/* Hero Section */}
                <section id="home-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="w-full"
                    >
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <motion.div
                            style={{ y: yText, opacity: opacityText }}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-left z-10"
                        >
                            <motion.div
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="inline-block mb-4 px-4 py-2 bg-sky-100 rounded-full"
                            >
                                <span className="text-sky-700 font-semibold text-sm">
                                    🚀 La plataforma #1 para tu negocio
                                </span>
                            </motion.div>

                            <motion.h1 
                                initial="hidden"
                                animate="visible"
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: {
                                        opacity: 1,
                                        transition: { staggerChildren: 0.1 }
                                    }
                                }}
                                className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight"
                            >
                                <motion.span variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="inline-block">Lleva</motion.span>{" "}
                                <motion.span variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="inline-block">tu</motion.span>{" "}
                                <motion.span variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }} className="inline-block">negocio</motion.span>
                                <br />
                                <motion.span 
                                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                                    className="bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent inline-block"
                                >
                                    al siguiente nivel
                                </motion.span>
                            </motion.h1>

                            <p className="text-xl text-gray-600 mb-8 max-w-lg">
                                Crea tu tienda virtual profesional en minutos. Gestiona pedidos, inventario,
                                cupones y más desde una sola plataforma moderna e intuitiva.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 justify-start items-center">
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Link
                                        id="btn-hero-cta"
                                        to="/login"
                                        className="group px-8 py-4 bg-gradient-to-r from-sky-600 to-blue-600 text-white rounded-full font-semibold text-lg hover:shadow-2xl transition-all flex items-center gap-2"
                                    >
                                        Comenzar Gratis
                                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                </motion.div>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Link
                                        id="btn-hero-secondary"
                                        to="/pricing"
                                        className="px-8 py-4 bg-white border-2 border-sky-700 text-sky-700 rounded-full font-semibold text-lg hover:bg-sky-50 hover:shadow-lg transition-all"
                                    >
                                        Ver Planes
                                    </Link>
                                </motion.div>
                            </div>
                        </motion.div>

                        {/* Spline 3D Scene */}
                        <motion.div
                            style={{ y: ySpline }}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="h-[500px] lg:h-[700px] w-full relative z-0 flex items-center justify-center lg:translate-x-10"
                        >
                            <ErrorBoundary fallback={<SplineFallback />}>
                                <Spline scene="https://prod.spline.design/x6fYNH7x-4C4x9ps/scene.splinecode?v=1" />
                            </ErrorBoundary>
                        </motion.div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-8 mt-20 max-w-4xl mx-auto border-t border-sky-100 pt-12 relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-center"
                        >
                            <div className="text-4xl font-bold text-sky-600 mb-1">100+</div>
                            <div className="text-gray-600 text-sm">Negocios Activos</div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="text-center"
                        >
                            <div className="text-4xl font-bold text-sky-600 mb-1">5K+</div>
                            <div className="text-gray-600 text-sm">Pedidos Procesados</div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="text-center"
                        >
                            <div className="text-4xl font-bold text-sky-600 mb-1">99.9%</div>
                            <div className="text-gray-600 text-sm">Uptime</div>
                        </motion.div>
                    </div>

                        {/* Logos de Fundadores (Ocultado temporalmente) */}
                        {/* <div className="mt-24 border-t border-sky-100 pt-12">
                            <p id="home-logos" className="text-center text-gray-400 font-semibold mb-10 text-xs sm:text-sm uppercase tracking-[0.2em]">
                                Negocios fundadores que confían en SkyMenu
                            </p>
                            <div className="w-full overflow-hidden relative py-4">
                                
                                <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-sky-50 via-sky-50/80 to-transparent z-10" />
                                <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-sky-50 via-sky-50/80 to-transparent z-10" />

                                <motion.div
                                    className="flex gap-16 md:gap-24 items-center w-max px-4"
                                    animate={{ x: ["0%", "-50%"] }}
                                    transition={{ repeat: Infinity, ease: "linear", duration: 40 }}
                                >
                                    
                                    {[...Array(2)].map((_, loopIndex) => (
                                        <div key={loopIndex} className="flex gap-16 md:gap-24 shrink-0">
                                            
                                            <div className="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity cursor-pointer transform hover:scale-110 duration-300">
                                                <img
                                                    src={optimizeSupabaseImage("https://ronioyggojmggvhkjdpj.supabase.co/storage/v1/object/public/product-images/business-logos/7efddb6d-efab-475a-b843-a2b12e10d2cf-logo-1739331002056.png", { width: 128, height: 128 })}
                                                    alt="SkyArt"
                                                    width="64" height="64"
                                                    className="w-16 h-16 rounded-full object-cover shadow-sm border-2 border-white ring-1 ring-gray-100"
                                                />
                                                <span className="font-bold text-gray-700 text-lg">SkyArt</span>
                                            </div>

                                            
                                            <div className="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity cursor-pointer transform hover:scale-110 duration-300">
                                                <img
                                                    src={optimizeSupabaseImage("https://ronioyggojmggvhkjdpj.supabase.co/storage/v1/object/public/product-images/business-logos/logos%20Ficti/mexican_grill_logo.png", { width: 128, height: 128 })}
                                                    alt="El Mariachi"
                                                    width="64" height="64"
                                                    className="w-16 h-16 rounded-full object-cover shadow-sm border-2 border-white ring-1 ring-gray-100 bg-white"
                                                />
                                                <span className="font-bold text-gray-700 text-lg">El Mariachi</span>
                                            </div>

                                            
                                            <div className="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity cursor-pointer transform hover:scale-110 duration-300">
                                                <img
                                                    src={optimizeSupabaseImage("https://ronioyggojmggvhkjdpj.supabase.co/storage/v1/object/public/product-images/business-logos/266b65ad-7b0d-4351-aead-79fe1b1d5af7-logo.jpg", { width: 128, height: 128 })}
                                                    alt="Eva's House"
                                                    width="64" height="64"
                                                    className="w-16 h-16 rounded-full object-cover shadow-sm border-2 border-white ring-1 ring-gray-100"
                                                />
                                                <span className="font-bold text-gray-700 text-lg">Eva's House</span>
                                            </div>

                                            
                                            <div className="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity cursor-pointer transform hover:scale-110 duration-300">
                                                <img
                                                    src={optimizeSupabaseImage("https://ronioyggojmggvhkjdpj.supabase.co/storage/v1/object/public/product-images/business-logos/logos%20Ficti/argentina_steakhouse_logo.png", { width: 128, height: 128 })}
                                                    alt="Pampa Grill"
                                                    width="64" height="64"
                                                    className="w-16 h-16 rounded-full object-cover shadow-sm border-2 border-white ring-1 ring-gray-100 bg-white"
                                                />
                                                <span className="font-bold text-gray-700 text-lg">Pampa Grill</span>
                                            </div>

                                            
                                            <div className="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity cursor-pointer transform hover:scale-110 duration-300">
                                                <img
                                                    src={optimizeSupabaseImage("https://ronioyggojmggvhkjdpj.supabase.co/storage/v1/object/public/product-images/business-logos/6a110095-ffe8-40ab-8607-e708fb340e41-logo.jpg", { width: 128, height: 128 })}
                                                    alt="Tu Combito"
                                                    width="64" height="64"
                                                    className="w-16 h-16 rounded-full object-cover shadow-sm border-2 border-white ring-1 ring-gray-100"
                                                />
                                                <span className="font-bold text-gray-700 text-lg">Tu Combito</span>
                                            </div>

                                            
                                            <div className="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity cursor-pointer transform hover:scale-110 duration-300">
                                                <img
                                                    src={optimizeSupabaseImage("https://ronioyggojmggvhkjdpj.supabase.co/storage/v1/object/public/product-images/business-logos/logos%20Ficti/usa_burger_joint_logo.png", { width: 128, height: 128 })}
                                                    alt="Liberty Burger"
                                                    width="64" height="64"
                                                    className="w-16 h-16 rounded-full object-cover shadow-sm border-2 border-white ring-1 ring-gray-100 bg-white"
                                                />
                                                <span className="font-bold text-gray-700 text-lg">Liberty Burger</span>
                                            </div>

                                            
                                            <div className="flex flex-col items-center gap-3 opacity-70 hover:opacity-100 transition-opacity cursor-pointer transform hover:scale-110 duration-300">
                                                <img
                                                    src={optimizeSupabaseImage("https://ronioyggojmggvhkjdpj.supabase.co/storage/v1/object/public/product-images/business-logos/52ee707b-3e3d-4a40-af76-9b2262b21c09-logo-1760986389056.jpg", { width: 128, height: 128 })}
                                                    alt="YA Detalles"
                                                    width="64" height="64"
                                                    className="w-16 h-16 rounded-full object-cover shadow-sm border-2 border-white ring-1 ring-gray-100"
                                                />
                                                <span className="font-bold text-gray-700 text-lg">YA Detalles</span>
                                            </div>
                                        </div>
                                    ))}
                                </motion.div>
                            </div>
                        </div> */}
                    </motion.div>
                </section>

                {/* Features Section - Bento Grid */}
                <section id="home-features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                            Potencia tu negocio con <br />
                            <span className="bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">herramientas inteligentes</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Todo lo que necesitas para vender más y gestionar mejor, diseñado con una experiencia de usuario de primer nivel.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[200px]">
                        {/* Feature 1: Tienda Virtual (Grande) */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="md:col-span-2 md:row-span-2"
                        >
                            <TiltCard className="h-full w-full bg-gradient-to-br from-sky-900 to-blue-900 rounded-3xl p-10 text-white shadow-xl overflow-hidden group">
                                <div className="relative z-10 h-full flex flex-col justify-between pointer-events-none">
                                    <div>
                                        <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6 border border-white/20 shadow-lg">
                                            <Store className="w-8 h-8 text-sky-300" />
                                        </div>
                                        <h3 className="text-3xl font-bold mb-4 text-white drop-shadow-md">Tienda Virtual Completa</h3>
                                        <p className="text-sky-100 text-lg max-w-md drop-shadow-sm">
                                            Crea tu menú digital profesional en minutos. Pasa el cursor por esta tarjeta para ver la magia de SkyMenu.
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 font-semibold text-white/90 transition-all pointer-events-auto cursor-pointer hover:text-white hover:gap-4 mt-8">
                                        Explorar configuración <ArrowRight className="w-5 h-5" />
                                    </div>
                                </div>
                            </TiltCard>
                        </motion.div>

                        {/* Feature 2: Pedidos */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -5 }}
                            className="bg-white p-8 rounded-3xl border border-sky-100 shadow-sm hover:shadow-xl transition-all group overflow-hidden relative"
                        >
                            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-sky-600 transition-colors">
                                <ShoppingBag className="w-6 h-6 text-sky-600 group-hover:text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Pedidos en Vivo</h3>
                            <p className="text-gray-600 text-sm">Gestiona comandas en tiempo real con notificaciones automáticas.</p>
                        </motion.div>

                        {/* Feature 3: Búsqueda IA */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            whileHover={{ y: -5 }}
                            className="bg-white p-8 rounded-3xl border border-sky-100 shadow-sm hover:shadow-xl transition-all group"
                        >
                            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-purple-600 transition-colors">
                                <Sparkles className="w-6 h-6 text-purple-600 group-hover:text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Búsqueda con IA</h3>
                            <p className="text-gray-600 text-sm">Inteligencia artificial que ayuda a tus clientes a encontrar su plato ideal.</p>
                        </motion.div>

                        {/* Feature 4: Dashboard (Largo) */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -5 }}
                            className="md:row-span-2 bg-gray-900 p-8 rounded-3xl text-white shadow-xl flex flex-col justify-between group overflow-hidden relative"
                        >
                            <div>
                                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-green-500 transition-colors">
                                    <TrendingUp className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold mb-4">Analíticas Avanzadas</h3>
                                <p className="text-gray-400">Visualiza tus ventas, productos top y crecimiento con gráficas interactivas.</p>
                            </div>
                            <div className="mt-8 pt-8 border-t border-white/10">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-green-400 font-bold">+24% este mes</span>
                                    <BarChart3 className="w-12 h-12 text-white/5 group-hover:text-white/20 transition-colors" />
                                </div>
                            </div>
                        </motion.div>

                        {/* Feature 5: Dominios */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            whileHover={{ y: -5 }}
                            className="bg-sky-50 p-8 rounded-3xl border border-sky-100 shadow-sm hover:shadow-xl transition-all group"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                    <Globe className="w-5 h-5 text-sky-600" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Dominios Pro</h3>
                            </div>
                            <p className="text-gray-600 text-sm">Tu propia dirección web única para fortalecer tu marca personal.</p>
                        </motion.div>

                        {/* Feature 6: Referidos */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            whileHover={{ y: -5 }}
                            className="bg-blue-50 p-8 rounded-3xl border border-blue-100 shadow-sm hover:shadow-xl transition-all group"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                    <Users className="w-5 h-5 text-blue-600" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Referidos</h3>
                            </div>
                            <p className="text-gray-600 text-sm">Crece orgánicamente con nuestro programa de referidos integrado.</p>
                        </motion.div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section id="home-benefits" className="bg-gradient-to-r from-sky-600 via-sky-700 to-blue-700 py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                            >
                                <h2 className="text-4xl font-bold text-white mb-6">
                                    ¿Por qué elegir SkyMenu?
                                </h2>
                                <p className="text-sky-100 text-lg mb-8">
                                    No somos solo otra plataforma. Somos tu aliado para el éxito digital.
                                </p>
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 px-8 py-4 bg-white text-sky-600 rounded-full font-semibold hover:shadow-2xl hover:scale-105 transition-all"
                                >
                                    Prueba Gratis por 30 días
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
                            >
                                <div className="space-y-4">
                                    {benefits.map((benefit, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: -10 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: index * 0.1 }}
                                            className="flex items-center gap-3"
                                        >
                                            <CheckCircle2 className="w-6 h-6 text-white flex-shrink-0" />
                                            <span className="text-white text-lg font-medium">{benefit}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* About Platform Section */}
                <section id="home-about" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-white overflow-hidden">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-50 to-blue-50 rounded-full text-sky-700 font-semibold text-sm mb-6 border border-sky-100 shadow-sm">
                                <Info className="w-4 h-4" />
                                Sobre la Plataforma
                            </div>
                            <h2 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
                                Transforma tu Negocio en una <br />
                                <span className="bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">Potencia Digital</span>
                            </h2>
                            <p className="text-lg text-gray-700 mb-6 leading-relaxed font-medium">
                                ¿Sabías que el 80% de los clientes prefieren negocios con presencia digital? SkyMenu no es solo una tienda virtual, es el motor de crecimiento que tu negocio necesita para destacar en la era moderna.
                            </p>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                Olvídate de los procesos manuales y las barreras técnicas. Te brindamos una suite de herramientas de nivel empresarial que automatizan tus ventas, organizan tu inventario y fidelizan a tus clientes, todo desde una interfaz tan simple que cualquiera puede usarla.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="group p-4 rounded-xl bg-sky-50 border border-sky-100 hover:shadow-md transition-all hover:-translate-y-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                                            <TrendingUp className="w-5 h-5 text-sky-600" />
                                        </div>
                                        <h4 className="font-bold text-gray-900">Escala Rápido</h4>
                                    </div>
                                    <p className="text-sm text-gray-600 leading-snug">Multiplica tus ventas con herramientas de marketing integradas.</p>
                                </div>
                                <div className="group p-4 rounded-xl bg-blue-50 border border-blue-100 hover:shadow-md transition-all hover:-translate-y-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                                            <Sparkles className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <h4 className="font-bold text-gray-900">Imagen Premium</h4>
                                    </div>
                                    <p className="text-sm text-gray-600 leading-snug">Tu marca se verá increíble y profesional en cualquier dispositivo.</p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative group"
                        >
                            <div className="absolute -inset-4 bg-gradient-to-tr from-sky-300 to-blue-400 rounded-3xl transform rotate-2 opacity-30 blur-xl group-hover:opacity-50 transition-opacity duration-500"></div>
                            <img
                                src="https://images.unsplash.com/photo-1556742049-0cfed4f7a07d?auto=format&fit=crop&q=80&w=800"
                                alt="Equipo trabajando con SkyMenu"
                                className="relative rounded-3xl shadow-2xl border-4 border-white transform transition-transform duration-500 group-hover:scale-[1.01]"
                            />
                            {/* Floating Stats Badge */}
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.5, type: "spring" }}
                                className="absolute -bottom-6 -right-6 bg-white p-4 rounded-2xl shadow-xl border border-gray-100 max-w-xs hidden md:block z-10"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="bg-green-100 p-3 rounded-full">
                                        <TrendingUp className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Crecimiento Promedio</p>
                                        <p className="text-xl font-black text-gray-900">+40% en Ventas</p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section id="home-faq" className="bg-gray-50 py-20">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-center mb-16"
                        >
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-sky-100 rounded-full mb-4">
                                <HelpCircle className="w-6 h-6 text-sky-600" />
                            </div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-4">
                                Preguntas Frecuentes
                            </h2>
                            <p className="text-xl text-gray-600">
                                Resolvemos tus dudas para que inicies con confianza
                            </p>
                        </motion.div>

                        <div className="space-y-6">
                            {faqs.map((faq, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                                >
                                    <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-start gap-3">
                                        <span className="text-sky-600 mt-1">
                                            <HelpCircle className="w-5 h-5" />
                                        </span>
                                        {faq.question}
                                    </h3>
                                    <p className="text-gray-600 pl-8 leading-relaxed">
                                        {faq.answer}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
                
                {/* Free Tools Section */}
                <section id="home-tools" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-white">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-sky-100 rounded-full mb-4">
                            <Wand2 className="w-6 h-6 text-sky-600" />
                        </div>
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Herramientas Gratuitas
                        </h2>
                        <p className="text-xl text-gray-600">
                            Impulsa tu negocio con nuestras herramientas diseñadas para emprendedores
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -10 }}
                            className="group bg-gradient-to-br from-white to-sky-50/30 p-8 rounded-3xl shadow-sm border border-sky-100 hover:shadow-2xl transition-all relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <QrCode className="w-32 h-32 text-sky-600" />
                            </div>
                            <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-sky-600 transition-colors">
                                <QrCode className="w-7 h-7 text-sky-600 group-hover:text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">Generador de QR</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Crea códigos QR profesionales para tu menú, redes sociales o sitio web en segundos. Totalmente gratis y sin registros.
                            </p>
                            <Link
                                to="/herramientas/qr"
                                className="inline-flex items-center gap-2 text-sky-600 font-bold hover:gap-3 transition-all"
                            >
                                Probar Ahora <ArrowRight className="w-5 h-5" />
                            </Link>
                        </motion.div>
                        
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -10 }}
                            transition={{ delay: 0.1 }}
                            className="group bg-gradient-to-br from-white to-blue-50/30 p-8 rounded-3xl shadow-sm border border-blue-100 hover:shadow-2xl transition-all relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <Calculator className="w-32 h-32 text-blue-600" />
                            </div>
                            <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                                <Calculator className="w-7 h-7 text-blue-600 group-hover:text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">Calculadora de Márgenes</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Optimiza tus precios y asegura la rentabilidad de cada venta con nuestra herramienta de cálculo profesional.
                            </p>
                            <Link
                                to="/herramientas/margen"
                                className="inline-flex items-center gap-2 text-blue-600 font-bold hover:gap-3 transition-all"
                            >
                                Calcular Ahora <ArrowRight className="w-5 h-5" />
                            </Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -10 }}
                            transition={{ delay: 0.2 }}
                            className="group bg-gradient-to-br from-white to-sky-50/30 p-8 rounded-3xl shadow-sm border border-sky-100 hover:shadow-2xl transition-all relative overflow-hidden"
                        >
                            <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center mb-6">
                                <Sparkles className="w-7 h-7 text-sky-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">Marketing Studio</h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Próximamente: Crea contenido visual impactante para tus redes sociales directamente desde SkyMenu.
                            </p>
                            <span className="inline-flex items-center px-3 py-1 bg-sky-100 text-sky-700 text-xs font-bold rounded-full">
                                PRÓXIMAMENTE
                            </span>
                        </motion.div>
                    </div>
                </section>

                {/* Support Section */}
                <section id="home-support" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-sky-100">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                ¿Necesitas ayuda?
                            </h2>
                            <p className="text-lg text-gray-600 mb-8">
                                Nuestro equipo de soporte está listo para acompañarte en cada paso. 
                                Ya sea una duda técnica o una consulta sobre tu plan, estamos a un mensaje de distancia.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm border border-sky-100">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                        <MessageSquare className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">WhatsApp</p>
                                        <a href="https://wa.me/17024973243" target="_blank" rel="noopener noreferrer" className="text-xl font-bold text-gray-900 hover:text-sky-600 transition-colors">
                                            +1 (702) 497-3243
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm border border-sky-100">
                                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                        <Mail className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Email</p>
                                        <a href="mailto:skymenu0@gmail.com" className="text-xl font-bold text-gray-900 hover:text-sky-600 transition-colors">
                                            skymenu0@gmail.com
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="bg-gradient-to-br from-sky-600 to-blue-700 p-8 rounded-3xl text-white shadow-2xl relative overflow-hidden"
                        >
                            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                            <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                            
                            <h3 className="text-2xl font-bold mb-4">Horario de Atención</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between border-b border-white/20 pb-2">
                                    <span>Lunes a Viernes</span>
                                    <span className="font-bold">9:00 AM - 6:00 PM</span>
                                </div>
                                <div className="flex justify-between border-b border-white/20 pb-2">
                                    <span>Sábados</span>
                                    <span className="font-bold">9:00 AM - 2:00 PM</span>
                                </div>
                                <div className="flex justify-between pt-2">
                                    <span>Domingos</span>
                                    <span className="opacity-70 italic">Cerrado</span>
                                </div>
                            </div>
                            <div className="mt-8 p-4 bg-white/10 rounded-2xl border border-white/20">
                                <p className="text-sm">
                                    Respuesta promedio en menos de 2 horas durante horario laboral.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-r from-sky-50 to-blue-100 rounded-3xl p-12 text-center border border-sky-200"
                    >
                        <BarChart3 className="w-16 h-16 text-sky-600 mx-auto mb-6" />
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            ¿Listo para comenzar?
                        </h2>
                        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                            Únete a cientos de negocios que ya están creciendo con SkyMenu.
                            Sin tarjeta de crédito. Sin compromiso.
                        </p>
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-sky-600 to-blue-600 text-white rounded-full font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all"
                        >
                            Crear mi Tienda Gratis
                            <ArrowRight className="w-6 h-6" />
                        </Link>
                    </motion.div>
                </section>
            </main>

            {/* Footer */}
            <footer className="bg-gradient-to-br from-sky-700 via-sky-600 to-blue-700 text-white py-12 mt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <Link to="/" className="flex items-center gap-2 mb-4 md:mb-0">
                            <img
                                src="https://imgur.com/WY80a5F.png"
                                alt="SkyMenu Logo"
                                className="h-16 w-auto"
                            />
                        </Link>
                        <div className="text-sky-100 text-center md:text-right">
                            <p>© 2024 SkyMenu. Todos los derechos reservados.</p>
                            <p className="text-sm mt-1">Hecho con ❤️ para emprendedores</p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
